/**
 * Synthetische Klang-Events: Pops, Burble, Up-/Downshift-Blips, G-Punch.
 * Erzeugt kurze Oszillator-/Noise-Bursts und mischt sie in den Master-Bus.
 */
import { state } from '../state/store.js'
import { resolveSoundEvent } from './sound-models.js'

const now = () => (state.ctx ? state.ctx.currentTime : 0)
const FALLBACK_SOUND_MODEL_ID = 'mustang-v8-crossplane'

export function eventShape(kind = 'pop', modelOrId = state.profile?.soundModelId || FALLBACK_SOUND_MODEL_ID) {
  try {
    return resolveSoundEvent(modelOrId, kind)
  } catch (e) {
    return resolveSoundEvent(FALLBACK_SOUND_MODEL_ID, kind)
  }
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function eventOutput() {
  return state.eventBus || state.master
}

/** Tiefer Anschlag beim Audiostart („Motor zündet"). */
export function unlockHit() {
  if (!state.ctx) return
  const shape = eventShape('startup')
  const c = state.ctx,
    o = c.createOscillator(),
    g = c.createGain()
  o.type = 'sine'
  o.frequency.value = shape.thumpHz[0]
  g.gain.setValueAtTime(0.0001, now())
  g.gain.exponentialRampToValueAtTime(state.mix.events * shape.gain, now() + 0.015)
  g.gain.exponentialRampToValueAtTime(0.0001, now() + clamp(shape.decayMs / 1000, 0.12, 0.5))
  o.connect(g).connect(eventOutput())
  o.start()
  o.stop(now() + clamp(shape.decayMs / 1000 + 0.02, 0.14, 0.55))
}

/**
 * Erzeugt ein Klang-Event.
 * @param {'shiftUp'|'shiftDown'|'burble'|'pop'|'gPunch'|'whine'|'bov'|'wastegate'} kind
 */
export function eventBoom(kind = 'pop') {
  if (!state.ctx) return
  try {
    const c = state.ctx,
      t = now()
    const shape = eventShape(kind)
    if (shape.gain <= 0 && shape.noiseGain <= 0) return
    const decaySec = clamp(shape.decayMs / 1000, 0.055, 0.42)
    const toneStop = clamp(decaySec * 0.88, 0.09, 0.34)
    const noiseStop = clamp(decaySec * (kind === 'bov' ? 1.0 : 0.72), 0.07, 0.32)
    const attackSec = kind === 'whine' || kind === 'bov' ? 0.018 : 0.012

    // Tonaler Thump (sinkende Sinus-Frequenz)
    const burst = c.createGain()
    burst.gain.setValueAtTime(0.0001, t)
    burst.gain.exponentialRampToValueAtTime(state.mix.events * shape.gain, t + attackSec)
    burst.gain.exponentialRampToValueAtTime(0.0001, t + toneStop)

    const thump = c.createOscillator()
    thump.type = kind === 'whine' ? 'triangle' : 'sine'
    thump.frequency.setValueAtTime(shape.thumpHz[0], t)
    thump.frequency.exponentialRampToValueAtTime(shape.thumpHz[1], t + clamp(decaySec * 0.68, 0.08, 0.26))
    thump.connect(burst).connect(eventOutput())
    thump.start(t)
    thump.stop(t + toneStop)

    // Gefiltertes Rauschen (Knall-Textur)
    const len = Math.max(1, Math.floor(c.sampleRate * noiseStop))
    const b = c.createBuffer(1, len, c.sampleRate),
      d = b.getChannelData(0)
    for (let i = 0; i < len; i++) {
      const x = i / len
      const env = Math.pow(1 - x, kind === 'burble' ? 4.2 : kind === 'bov' ? 2.6 : 3.9)
      d[i] = (Math.random() * 2 - 1) * env * (0.42 + shape.density * 0.48)
    }
    const n = c.createBufferSource()
    n.buffer = b
    const hp = c.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = kind === 'bov' ? 240 : kind === 'wastegate' ? 180 : kind === 'burble' ? 100 : 135
    const lp = c.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = clamp(460 + shape.brightness * 1320 + shape.density * 220, 420, 2400)
    const ng = c.createGain()
    ng.gain.setValueAtTime(state.mix.events * shape.noiseGain * (0.7 + shape.density * 0.55), t)
    ng.gain.exponentialRampToValueAtTime(0.0001, t + clamp(noiseStop * 0.82, 0.06, 0.24))
    n.connect(hp).connect(lp).connect(ng).connect(eventOutput())
    n.start(t)
    n.stop(t + noiseStop)

    // Zwischengas-Blip beim Runterschalten
    if (kind === 'shiftDown' && shape.blip > 0) {
      const blipG = c.createGain()
      blipG.gain.setValueAtTime(0.0001, t)
      blipG.gain.exponentialRampToValueAtTime(state.mix.events * shape.blip, t + 0.018)
      blipG.gain.exponentialRampToValueAtTime(0.0001, t + 0.115)
      const blip = c.createOscillator()
      blip.type = 'triangle'
      blip.frequency.setValueAtTime(132, t)
      blip.frequency.exponentialRampToValueAtTime(96, t + 0.12)
      blip.connect(blipG).connect(eventOutput())
      blip.start(t)
      blip.stop(t + 0.13)
    }
  } catch (e) {
    // Events sind unkritisch – nie die Render-Loop crashen lassen.
  }
}
